import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../api/client";
import { useLang } from "../i18n/LangContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { HardDrive, ImageDown, PiggyBank, Gauge, Cloud, Loader2, CheckCircle2 } from "lucide-react";

const fmtBytes = (n) => {
  n = n || 0;
  if (n < 1024) return `${n} B`;
  const u = ["KB", "MB", "GB", "TB"];
  let i = -1;
  do { n /= 1024; i++; } while (n >= 1024 && i < u.length - 1);
  return `${n.toFixed(n >= 100 ? 0 : 1)} ${u[i]}`;
};

const prettyKind = (k) =>
  (k || "OTHER").toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function StorageSavings() {
  const { t } = useLang();
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard/storage-stats").then(({ data }) => setS(data.data)).catch(() => {});
  }, []);
  if (!s) return null;

  const pct = Math.round((s.avg_compression_ratio || 0) * 100);
  const kpis = [
    { icon: ImageDown, label: t("storage.totalPhotos"), value: s.total_files, sub: `${s.optimized_files} ${t("storage.optimized")}`, accent: "text-sky-300", testId: "storage-total-photos" },
    { icon: HardDrive, label: t("storage.stored"), value: fmtBytes(s.total_compressed_bytes), sub: `${t("storage.original")} ${fmtBytes(s.total_original_bytes)}`, accent: "text-slate-200", testId: "storage-stored" },
    { icon: PiggyBank, label: t("storage.saved"), value: fmtBytes(s.total_saved_bytes), sub: t("storage.subtitle"), accent: "text-emerald-300", testId: "storage-saved" },
    { icon: Gauge, label: t("storage.avgRatio"), value: `${pct}%`, sub: `${s.optimized_files} ${t("storage.optimized")}`, accent: "text-[#F59E0B]", testId: "storage-avg-ratio" },
  ];

  return (
    <div data-testid="storage-savings-panel" className="space-y-4">
      <div className="flex items-center gap-2">
        <PiggyBank className="h-4 w-4 text-emerald-300" />
        <h2 className="font-[Outfit] font-bold text-white text-lg">{t("storage.title")}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="bg-[#161923] border-white/5 hover:border-white/15 transition h-full">
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-medium flex items-center gap-2"><k.icon className="h-3.5 w-3.5" /> {k.label}</div>
              <div data-testid={k.testId} className={`mt-3 font-[Outfit] text-3xl font-extrabold ${k.accent}`}>{k.value}</div>
              <div className="text-xs text-slate-500 mt-1 truncate">{k.sub}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {(s.by_kind || []).length > 0 && (
        <Card className="bg-[#161923] border-white/5">
          <CardContent className="p-0">
            <div className="px-5 py-4 border-b border-white/5 font-[Outfit] font-bold text-white text-sm">{t("storage.byKind")}</div>
            <div className="overflow-x-auto -mx-1 px-1">
              <table data-testid="storage-kind-table" className="w-full min-w-[520px] text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500">
                  <tr className="border-b border-white/5">
                    <th className="text-left px-5 py-3 font-medium">{t("storage.kind")}</th>
                    <th className="text-right px-3 py-3 font-medium">{t("storage.totalPhotos")}</th>
                    <th className="text-right px-3 py-3 font-medium">{t("storage.original")}</th>
                    <th className="text-right px-3 py-3 font-medium">{t("storage.stored")}</th>
                    <th className="text-right px-5 py-3 font-medium">{t("storage.saved")}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.by_kind.map((k) => (
                    <tr key={k.kind} data-testid={`storage-kind-${k.kind}`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                      <td className="px-5 py-3 text-white font-medium">{prettyKind(k.kind)}<span className="text-slate-500 font-normal"> · {k.optimized}✓</span></td>
                      <td className="px-3 py-3 text-right text-slate-300">{k.count}</td>
                      <td className="px-3 py-3 text-right text-slate-400">{fmtBytes(k.original)}</td>
                      <td className="px-3 py-3 text-right text-slate-200">{fmtBytes(k.compressed)}</td>
                      <td className="px-5 py-3 text-right text-emerald-300 font-semibold">{fmtBytes(k.saved)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <R2Backfill />
    </div>
  );
}

function R2Backfill() {
  const { t } = useLang();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/storage/backfill-r2/status").then(({ data }) => setS(data.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (s?.state !== "RUNNING") return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [s?.state]);

  const run = async () => {
    setBusy(true);
    try {
      await api.post("/admin/storage/backfill-r2");
      toast.success(t("storage.backfillStarted"));
      setTimeout(load, 800);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.detail?.message || "Error");
    } finally { setBusy(false); }
  };

  const running = s?.state === "RUNNING";
  return (
    <Card data-testid="r2-backfill-panel" className="bg-[#161923] border-white/5">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-[Outfit] font-bold text-white text-sm flex items-center gap-2"><Cloud className="h-4 w-4 text-sky-300" /> {t("storage.cdnTitle")}</div>
            <div className="text-xs text-slate-500 mt-1">{t("storage.cdnDesc")}</div>
          </div>
          <span data-testid="r2-status-badge" className={`rounded-full border px-3 py-1 text-xs font-semibold ${s?.r2_enabled ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
            {s?.r2_enabled ? t("storage.cdnEnabled") : t("storage.cdnDisabled")}
          </span>
        </div>

        {s?.state && s.state !== "IDLE" && (
          <div className="grid grid-cols-3 gap-3 text-center">
            {[[t("storage.backfillMigrated"), s.migrated || 0, "text-sky-300"], [t("storage.backfillThumbs"), s.thumbs || 0, "text-emerald-300"], [t("storage.backfillErrors"), s.errors || 0, "text-red-300"]].map(([label, val, c]) => (
              <div key={label} className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                <div className={`font-[Outfit] text-2xl font-extrabold ${c}`}>{val}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button data-testid="r2-backfill-btn" onClick={run} disabled={busy || running || !s?.r2_enabled}
            className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold disabled:opacity-50">
            {running ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> {t("storage.backfillRunning")}</> : t("storage.backfillRun")}
          </Button>
          {s?.state === "DONE" && <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> {t("storage.backfillDone")}</span>}
        </div>
      </CardContent>
    </Card>
  );
}
